import { ClockCircleOutlined, LockOutlined } from "@ant-design/icons";
import { Link, useNavigate } from "@tanstack/react-router";
import { Button } from "antd";
import { useGetTenantAccessStatusQuery, useRequestAccessMutation } from "api/Tenant/hooks";
import { AxiosError } from "axios";
import { AuthButton } from "components/UI/AuthButton/AuthButton.tsx";
import { AuthPageContainer } from "components/UI/AuthPageContainer/AuthPageContainer.tsx";
import { RegistrationSuccess } from "components/UI/RegistrationSuccess/RegistrationSuccess.tsx";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { useUser } from "hooks/useUser.ts";
import { FC, useState } from "react";
import { toast } from "react-toastify";
import { CatalogStatus } from "types/enums";
import { ApiErrorResponse } from "types/models.ts";
import { extractValidationErrorMessages } from "utils/extractValidationErrorMessages.ts";

interface NoTenantAccessPageProps {
    tenantSlug?: string;
    tenantId?: string;
    // Distinct from the /account/programs catalog flow, which is gated by show_in_public_catalog
    // instead — see TenantService::requestAccess() on the backend for why these are separate.
    allowAccessRequests?: boolean;
}

export const NoTenantAccessPage: FC<NoTenantAccessPageProps> = ({ tenantSlug, tenantId, allowAccessRequests }) => {
    const { t } = usePaldockTranslation("pages.no-tenant-access");
    const { t: tProgramsCatalog } = usePaldockTranslation("tables.programs-catalog");
    const userUtils = useUser();
    const navigate = useNavigate();

    const [requestSubmitted, setRequestSubmitted] = useState(false);
    const requestAccessMutation = useRequestAccessMutation();

    // Backed by the server (not just local state) so a page refresh keeps showing the pending
    // screen instead of falling back to "No access" — access-status isn't gated by
    // show_in_public_catalog, unlike catalogShow(), so it works for this flow's tenants too.
    const accessStatusQuery = useGetTenantAccessStatusQuery(tenantId ?? "", { enabled: !!tenantId });
    const isPending = accessStatusQuery.data?.tenant.catalog_status === CatalogStatus.Pending;

    const handleRequestAccess = () => {
        if (!tenantId) {
            return;
        }

        requestAccessMutation.mutate(
            { tenantId },
            {
                onSuccess: (data) => {
                    if (data.joined && tenantSlug) {
                        toast.success(tProgramsCatalog("messages.joined"));
                        navigate({ to: "/$tenantSlug", params: { tenantSlug } });
                    } else {
                        setRequestSubmitted(true);
                    }
                },
                onError: (error: AxiosError<ApiErrorResponse>) =>
                    toast.error(extractValidationErrorMessages(error)[0] ?? tProgramsCatalog("messages.error")),
            }
        );
    };

    // Same "pending approval" screen used by ActivateForm after central registration — reused
    // here so a request from the no-access page reads the same way as one from sign-up.
    if (requestSubmitted || isPending) {
        return (
            <AuthPageContainer>
                <RegistrationSuccess
                    message={tProgramsCatalog("messages.request_submitted")}
                    icon={<ClockCircleOutlined style={{ fontSize: "167px", color: "#FFA940" }} />}
                    alertType="warning"
                />
            </AuthPageContainer>
        );
    }

    const showRequestAccess = allowAccessRequests && !!tenantId;

    return (
        <AuthPageContainer title={t("title")}>
            <RegistrationSuccess
                message={t("subtitle")}
                icon={<LockOutlined style={{ fontSize: 130, color: "#FF4D4F" }} />}
                alertType="warning"
            />
            <div className="flex flex-col gap-3 w-full">
                {showRequestAccess ? (
                    <>
                        <AuthButton
                            htmlType="button"
                            loading={requestAccessMutation.isPending}
                            onClick={handleRequestAccess}
                        >
                            {tProgramsCatalog("actions.request_access")}
                        </AuthButton>
                        <Button block onClick={() => userUtils.logoutUser(tenantSlug)}>
                            {t("actions.use_different_account")}
                        </Button>
                    </>
                ) : (
                    <AuthButton htmlType="button" onClick={() => userUtils.logoutUser(tenantSlug)}>
                        {t("actions.use_different_account")}
                    </AuthButton>
                )}
                <Link to="/account" className="text-center text-sm text-black underline hover:no-underline">
                    {t("actions.back_to_account")}
                </Link>
            </div>
        </AuthPageContainer>
    );
};
